import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import {
  AlertTriangle,
  ArrowRightLeft,
  Check,
  Clock,
  ExternalLink,
  FileText,
  FileUp,
  List,
  Loader2,
  Map as MapIcon,
  Pencil,
  RefreshCw,
  Sparkles,
  Trash2,
} from "lucide-react";
import { Fragment, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { api, savedExercise, streamReceivedSuggestions } from "../../api/client";
import type {
  EnemyType,
  Exercise,
  ExerciseSaved,
  Level,
  ReceivedApply,
  ReceivedMissionChoices,
  ReceivedOrder,
  ReceivedSuggestions,
  ReceivedSummary,
  ReceivedWeather,
} from "../../api/types";
import { attentionSelector, designate } from "../../components/attention";
import { orderActions, refreshOrders, withRefreshed } from "../../map/orderSync";
import { Banner, Callout } from "../../components/Callout";
import { Checkbox } from "../../components/Checkbox";
import { Collapsible } from "../../components/Collapsible";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { DropZone, useFileDrop } from "../../components/DropZone";
import { EmptyState, Spinner } from "../../components/EmptyState";
import { motion, Motion, Presence } from "../../components/presence";
import { Badge, Button, ButtonLink, IconButton, inputTitleClass, SectionLabel } from "../../components/ui";
import { useDismiss } from "../../components/useDismiss";
import { useMediaQuery } from "../../components/useMediaQuery";
import { ViewHeader } from "../../components/ViewHeader";
import { unitDesignation } from "../../i18n/designation";
import { k, locale, t, tServer } from "../../i18n/i18n";
import { useTrialExport } from "../../licence/trial";
import { missionName, term, unitMissionName } from "../../i18n/vocabulary";
import { plural } from "../../plural";
import { useUi } from "../../state/ui";
import { DossierEmptyState } from "../DossierEmptyState";
import { type FieldKey, rebaseStoredDraft, storedDraftFor } from "../missionDraft";
import { ImportDialog } from "./ImportDialog";
import { ChiefOrderBanner } from "./ChiefOrderNotice";
import { changedSet, markLines, type ReceivedFocus, useReceivedFocus } from "./chiefNotice";
import { designateDossierReadOnly, useWriteBlock } from "../../sharing/access";
import { Waiting } from "../../components/Waiting";
import { SEASONAL, seasonalLabel } from "../../weather";
import { translationInterval } from "../translation/TranslationBanner";

/** Ordres reçus (Documents) : l'« Analyse d'un ordre reçu » de l'iPad, conservée dans le dossier. L'ordre importé
 *  (PDF, Word, LibreOffice, RTF, texte ou texte collé) est découpé en paragraphes ; mission, émetteur, ennemi,
 *  tâches et contraintes sont cités tels quels ; ce qui peut remplir les Données initiales se reporte case par
 *  case ; le modèle local peut proposer une synthèse et des tâches implicites, à vérifier. */

const when = (iso: string | null | undefined) => {
  if (!iso) return "";
  const d = new Date(iso);
  return t("le {date} à {time}", {
    date: d.toLocaleDateString(locale(), { day: "2-digit", month: "2-digit" }),
    time: d.toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" }),
  });
};

/** « 1(AZ)DM · division » ; l'échelon seul quand l'émetteur n'est désigné que par lui (« GTIA »). */
const issuerLabel = (unit: string | null, echelon: string | null) => {
  const shown = echelon && tServer(echelon); // échelon rédigé par le serveur : comparé tel quel, traduit à l'affichage
  const name = unit && unitDesignation(unit);
  return !unit ? shown : !echelon || echelon.toUpperCase().includes(unit.toUpperCase()) ? (shown ?? name) : `${name} · ${shown}`;
};

/** Carte de la fiche d'un ordre : titre en 13 px semi-gras, casse normale (R1), ombre de carte posée (R3). */
function Card({ title, icon, children, trailing }: { title: string; icon?: ReactNode; children: ReactNode; trailing?: ReactNode }) {
  return (
    <section className="overflow-hidden rounded-xl border border-line bg-surface shadow-card">
      <header className="flex min-h-10 flex-wrap items-center gap-2 border-b border-line px-4 py-2">
        {icon}
        <h3 className="min-w-0 flex-1 text-title leading-snug font-semibold text-ink">{title}</h3>
        {trailing}
      </header>
      {children}
    </section>
  );
}

/** Lignes d'un texte, celles qui ont changé depuis la dernière version vue surlignées (ordres transmis prévenus). */
function MarkedLines({ text, changed }: { text: string; changed: ReadonlySet<string> }) {
  return (
    <>
      {markLines(text, changed).map((l, i) => (
        <Fragment key={i}>
          {i > 0 && "\n"}
          {l.changed ? (
            <mark data-changed-line="" className="rounded-sm bg-mark-soft px-0.5 text-ink">
              {l.line}
            </mark>
          ) : (
            l.line
          )}
        </Fragment>
      ))}
    </>
  );
}

/** Texte cité, la forme du verbe de mission mise en évidence ; lignes changées depuis la dernière version vue
 *  surlignées (`changed`). */
function Quoted({ text, highlight, changed }: { text: string; highlight?: string | null; changed?: ReadonlySet<string> }) {
  const at = highlight ? text.indexOf(highlight) : -1;
  const marked = Boolean(changed && markLines(text, changed).some((l) => l.changed));
  const body = marked ? (
    <MarkedLines text={text} changed={changed!} />
  ) : at < 0 ? (
    text
  ) : (
    <>
      {text.slice(0, at)}
      <mark className="rounded-sm bg-accent-soft px-0.5 font-semibold text-accent">{highlight}</mark>
      {text.slice(at + highlight!.length)}
    </>
  );
  // Justifié à partir de 640 px seulement : sur un écran étroit, les lézardes de justification gênent la lecture.
  return <p className="text-left text-title leading-relaxed whitespace-pre-line text-ink hyphens-auto sm:text-justify">{body}</p>;
}

// --- Report dans les Données initiales ------------------------------------------------------------------------

/** Saisie non enregistrée des Données initiales gardée sur ce poste pour ce dossier (`missionDraft.ts`). */
function draftPending(exercise: Exercise): boolean {
  return storedDraftFor(exercise) !== null;
}

/** Ligne du report : case à cocher (si la donnée peut être reportée), rubrique en petites capitales, valeur. */
function ApplyRow({
  label,
  checked,
  disabled,
  onChange,
  children,
}: {
  label: string;
  checked?: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
  children: ReactNode;
}) {
  return (
    <div className={clsx("flex gap-3 px-4 py-2.5", disabled && "opacity-60")}>
      <div className="w-5 shrink-0 pt-0.5">
        {onChange && <Checkbox checked={Boolean(checked)} disabled={disabled} onChange={onChange} aria-label={label} />}
      </div>
      <div className="min-w-0 flex-1">
        <SectionLabel inset>{label}</SectionLabel>
        <div className="mt-0.5 text-ui leading-snug text-ink">{children}</div>
      </div>
    </div>
  );
}

/** Valeur reportée, rédigée par le serveur (« Régiment », « Attaquer (Missions offensives) ») : niveau, mission et
 *  mode d'action dans la langue de l'interface ; force et ennemi sont des noms propres. */
function appliedValue(x: { key: string; value: string }): string {
  if (x.key === "level") return tServer(x.value);
  if (x.key === "weather") return x.value === SEASONAL ? seasonalLabel() : x.value;
  if (x.key !== "mission") return x.value;
  const parts = /^(.*) \((.*)\)$/.exec(x.value);
  return parts ? `${missionName(parts[1])} (${term(parts[2])})` : missionName(x.value);
}

export type { ReceivedWeather };

/** Champ « Météo » d'un ordre reçu ; null quand la météo de l'ordre est inconnue (fichier importé, texte collé, dossier
 *  remis par code) ou d'une réponse d'avant ce champ. */
export function weatherReport(order: Pick<ReceivedOrder, "fields">): ReceivedWeather | null {
  return order.fields.weather ?? null;
}

/** Libellé d'une météo rédigée par le serveur : « Saisonnière » pour la météo de saison. */
export function weatherLabel(label: string): string {
  return label === SEASONAL ? seasonalLabel() : label;
}

/** Champs des Données initiales que le report remplace dans la saisie gardée sur le poste : la météo reportée, ou
 *  remise à la saisonnière par le passage en CENTAC. */
export function weatherReported(weather: boolean, centacLevel: boolean): FieldKey[] {
  return weather || centacLevel ? ["parameters.weather_scenario", "parameters.weather_custom"] : [];
}

export function ApplyCard({ exercise, order }: { exercise: Exercise; order: ReceivedOrder }) {
  // Dossier en lecture seule pour le compte (docs/17 §3.2) : écritures bloquées, l'appui désigne le bandeau.
  const writeBlock = useWriteBlock();
  const queryClient = useQueryClient();
  const notify = useUi((s) => s.notify);
  const setView = useUi((s) => s.setView);
  const setMapSlug = useUi((s) => s.setMapSlug);
  const setDeploymentParams = useUi((s) => s.setDeploymentParams);
  const f = order.fields;
  // Ennemi relevé : null pour une force non française (ennemi générique, registre B39) — la ligne « Ennemi » n'est
  // pas proposée.
  const enemyField = f.enemy;
  // Météo de l'ordre du chef : null quand elle est inconnue — la ligne « Météo » n'est pas proposée.
  const weather = weatherReport(order);
  const levels = f.level.labels;
  const applyTarget = `received-apply-${order.id}`;
  const [levelOn, setLevelOn] = useState(false);
  const [forceOn, setForceOn] = useState(false);
  const [missionChoice, setMissionChoice] = useState<string | null>(null); // null : la première citée
  const [enemyOn, setEnemyOn] = useState(Boolean(enemyField?.proposed && enemyField.proposed !== enemyField.current));
  // Cochée d'office quand l'ordre du chef a une autre météo que le dossier, comme l'ennemi : la météo vient de l'échelon
  // supérieur ; elle ne se reporte qu'à l'appui sur « Reporter ».
  const [weatherOn, setWeatherOn] = useState(Boolean(weather?.applicable));

  const target: Level | null = levelOn ? f.level.proposed : f.level.current;
  const centac = target === "CENTAC";
  const force = levelOn ? f.force.at_proposed : f.force.at_level;
  const choices: ReceivedMissionChoices | null | undefined = levelOn
    ? f.mission.proposed
    : forceOn && f.mission.current_with_force
      ? f.mission.current_with_force
      : f.mission.current;
  const options = choices?.options ?? [];
  const choicesNote = choices?.note;
  // Par défaut, la mission du dossier si l'ordre la cite (rien à changer), sinon la première citée.
  const fallback = options.find((o) => o.name === f.mission.value && !levelOn && !forceOn) ?? options[0] ?? null;
  const mission = missionChoice === "none" ? null : (options.find((o) => o.name === missionChoice) ?? fallback);
  const missionChanges = Boolean(mission && (mission.name !== f.mission.value || levelOn || forceOn));
  const enemyApplicable = Boolean(enemyField?.proposed && !centac);
  const enemyChanges = enemyOn && enemyApplicable && enemyField?.proposed !== enemyField?.current;
  // En CENTAC, la météo se rédige dans l'ordre de la rotation : rien à reporter (le passage la remet à la saisonnière).
  const weatherApplicable = Boolean(weather?.applicable && !centac);
  const weatherChanges = weatherOn && weatherApplicable;
  const count = [levelOn, forceOn && Boolean(force), missionChanges, enemyChanges, weatherChanges].filter(Boolean).length;
  const resetsLayout = levelOn || (forceOn && Boolean(force)) || missionChanges;
  // L'ordre ne propose rien de plus que ce que contient déjà le dossier.
  const concordant =
    !f.level.applicable &&
    !f.force.at_level &&
    (options.length === 0 || options.some((o) => o.name === f.mission.value)) &&
    (!enemyField?.proposed || enemyField.proposed === enemyField.current) &&
    !weather?.applicable;

  const apply = useMutation({
    mutationFn: () => {
      const body: ReceivedApply = {
        level: levelOn,
        force: forceOn && Boolean(force) && !levelOn,
        mission: missionChanges ? mission!.name : null,
        enemy_type: enemyChanges,
        weather: weatherChanges,
        version: exercise.version,
      };
      return api.applyReceivedOrder(exercise.id, order.id, body);
    },
    onSuccess: ({ exercise: raw, order: next }) => {
      // Réponse de `PATCH /exercises` : `orders` y dit les OPO recomposés par le report (l'ennemi, le niveau…), rangés
      // à part (`refreshed`) sans remplacer le décompte de la liste des dossiers.
      const updated = savedExercise(raw as unknown as ExerciseSaved);
      const { refreshed, ...saved } = updated;
      queryClient.setQueryData(["received", exercise.id, order.id], next);
      queryClient.setQueryData<Exercise[]>(["exercises"], (list) => list?.map((e) => (e.id === saved.id ? { ...e, ...saved } : e)));
      if (refreshed) refreshOrders(queryClient, saved.id);
      void queryClient.invalidateQueries({ queryKey: ["exercises"] });
      void queryClient.invalidateQueries({ queryKey: ["received", exercise.id], exact: true });
      // Ordre du chef reporté : son avis de changement tombe (ordres transmis prévenus).
      void queryClient.invalidateQueries({ queryKey: ["chain-notice", exercise.id] });
      // Saisie non enregistrée des Données initiales : gardée, sauf pour les données reportées (choix explicite).
      const reported: FieldKey[] = [];
      // Niveau : force et mission recalculées par le serveur, l'ODB ennemi suit s'il change ; force : sa mission.
      if (levelOn) reported.push("parameters.level", "parameters.odb", "parameters.mission", "mapSlug");
      if (forceOn && Boolean(force)) reported.push("parameters.odb", "parameters.mission");
      if (missionChanges) reported.push("parameters.mission");
      if (enemyChanges) reported.push("parameters.enemy_type");
      reported.push(...weatherReported(weatherChanges, levelOn && centac));
      rebaseStoredDraft(updated, reported);
      if (updated.map_slug !== exercise.map_slug) setMapSlug(updated.map_slug);
      setDeploymentParams(null); // la carte reprend le dispositif enregistré
      // Dispositif relu : la prochaine écriture de la carte part de la nouvelle version du dossier (pas de faux
      // « modifié ailleurs »).
      void queryClient.invalidateQueries({ queryKey: ["deployment"] });
      setLevelOn(false);
      setForceOn(false);
      setMissionChoice(null);
      setWeatherOn(false);
      // Avis de l'enregistrement (météo ramenée au terrain, météo choisie retirée en CENTAC…) : annoncés par le client
      // de l'API au relais de la carte, qui les joint une seule fois à cet avis.
      notify({
        text: t("Données initiales mises à jour"),
        detail: withRefreshed(
          next.applied?.fields.map((x) => t("{label} : {value}", { label: tServer(x.label), value: appliedValue(x) })).join(" · ") || undefined,
          refreshed,
        ),
        tone: "ok",
        action: { label: t("Ouvrir"), run: () => setView("mission") },
        // OPO recomposés par le report : « Voir les changements » (ou « Voir l'ordre », « Ouvrir l'OPO »).
        secondary: refreshed ? orderActions(undefined, refreshed).action : undefined,
      });
    },
    onError: (e: Error) => notify({ text: t("Report impossible"), detail: e.message, tone: "error" }),
  });

  const enemyNote = (enemy: NonNullable<typeof enemyField>, type: EnemyType | null) =>
    enemy.source === "faction"
      ? t("codes de faction « ({code}) » des unités ennemies", { code: factionCode(type) })
      : t("Pertinence pour cet ennemi : {score} points, {percent} %", {
          score: enemy.score,
          percent: Math.round(enemy.confidence * 100),
        });

  return (
    <Card
      title={t("Report dans les Données initiales")}
      icon={<ArrowRightLeft size={14} className="text-ink-muted" />}
      trailing={
        <Button variant="link" onClick={() => setView("mission")}>
          <MapIcon size={14} /> {t("Données initiales")}
        </Button>
      }
    >
      <div className="divide-y divide-line" data-attention={applyTarget}>
        <ApplyRow label={t("Niveau")} checked={levelOn} disabled={!f.level.applicable} onChange={f.level.applicable ? setLevelOn : undefined}>
          {f.level.proposed ? (
            <>
              <span className="font-semibold">{tServer(levels[f.level.proposed])}</span>
              <span className="text-ink-soft"> — {tServer(f.level.hints[f.level.proposed])}</span>
            </>
          ) : (
            <span className="text-ink-soft">{t("Aucun niveau proposé")}</span>
          )}
          <p className="mt-0.5 text-meta text-ink-muted">
            {f.level.unit &&
              `${t("Émetteur : {issuer}, d'après {clue}.", {
                issuer: issuerLabel(f.level.unit, f.level.echelon),
                clue: f.level.clue && tServer(f.level.clue),
              })} `}
            {f.level.note != null
              ? tServer(f.level.note)
              : f.level.current
                ? t("Actuel : {value}.", { value: tServer(levels[f.level.current]) })
                : ""}
          </p>
        </ApplyRow>
        {levelOn ? (
          <ApplyRow label={t("Force commandée")}>
            {f.force.at_proposed ? (
              <span className="font-semibold">{f.force.at_proposed.label}</span>
            ) : (
              <span className="text-ink-soft">{t("La première du niveau (aucune force de ce niveau n'est désignée par l'ordre)")}</span>
            )}
          </ApplyRow>
        ) : (
          f.force.at_level && (
            <ApplyRow label={t("Force commandée")} checked={forceOn} onChange={setForceOn}>
              <span className="font-semibold">{f.force.at_level.label}</span>
              <p className="mt-0.5 text-meta text-ink-muted">
                {t("Désignée par l'émetteur de l'ordre. Actuelle : {current}.", { current: f.force.current?.label ?? "—" })}
              </p>
            </ApplyRow>
          )
        )}
        <ApplyRow label={t("Mission")}>
          {centac ? (
            <span className="text-ink-soft">{t("Rotation CENTAC : la mission de chaque OPO suit la rotation.")}</span>
          ) : options.length === 0 ? (
            <span className="text-ink-soft">
              {choicesNote != null ? tServer(choicesNote) : t("Aucune mission du niveau citée par l'ordre.")}
            </span>
          ) : (
            <div className="mt-1 space-y-1" role="radiogroup" aria-label={t("Mission à reporter")}>
              {options.map((o) => (
                <label key={o.name} className="flex cursor-pointer items-baseline gap-2">
                  <input
                    type="radio"
                    name={`mission-${order.id}`}
                    checked={mission?.name === o.name}
                    onChange={() => setMissionChoice(o.name)}
                    className="accent-accent"
                  />
                  <span>
                    <span className="font-semibold">{missionName(o.name)}</span>
                    <span className="text-ink-soft"> ({term(o.mode)})</span>
                    <span className="text-ink-muted"> — {t("cité « {quote} »", { quote: o.quote })}</span>
                    {o.name === f.mission.value && !levelOn && !forceOn && (
                      <span className="text-ink-muted"> · {t("déjà dans le dossier")}</span>
                    )}
                  </span>
                </label>
              ))}
              <label className="flex cursor-pointer items-baseline gap-2 text-ink-soft">
                <input
                  type="radio"
                  name={`mission-${order.id}`}
                  checked={mission === null}
                  onChange={() => setMissionChoice("none")}
                  className="accent-accent"
                />
                {t("Ne pas reporter la mission")}
              </label>
              {choices?.origin && <p className="text-meta text-ink-muted">{tServer(choices.origin)}</p>}
            </div>
          )}
          {!centac && f.mission.value && (
            <p className="mt-1 text-meta text-ink-muted">
              {t("Actuelle : {value}.", {
                value: `${missionName(f.mission.value)}${f.mission.mode ? ` (${term(f.mission.mode)})` : ""}`,
              })}
            </p>
          )}
        </ApplyRow>
        {enemyField && (
          <ApplyRow
            label={t("Ennemi")}
            checked={enemyOn && enemyApplicable}
            disabled={!enemyApplicable || enemyField.proposed === enemyField.current}
            onChange={enemyApplicable && enemyField.proposed !== enemyField.current ? setEnemyOn : undefined}
          >
            {centac ? (
              <span className="text-ink-soft">{t("Rotation CENTAC : ennemi Titan imposé (et camp de Mailly).")}</span>
            ) : enemyField.proposed ? (
              <>
                <span className="font-semibold">{enemyField.proposed}</span>
                <span className="text-ink-muted"> — {enemyNote(enemyField, enemyField.proposed)}</span>
                {enemyField.proposed === enemyField.current && <span className="text-ink-muted"> · {t("déjà dans le dossier")}</span>}
              </>
            ) : Object.keys(enemyField.scores).length ? (
              <span className="text-ink-soft">
                {t("Indices non concluants ({scores}) : le type d'ennemi reste à choisir.", {
                  scores: Object.entries(enemyField.scores)
                    .map(([name, score]) => `${name} ${score}`)
                    .join(", "),
                })}
              </span>
            ) : (
              <span className="text-ink-soft">{t("Aucun indice sur le type d'ennemi.")}</span>
            )}
            {!centac && enemyField.current && enemyField.proposed !== enemyField.current && (
              <p className="mt-0.5 text-meta text-ink-muted">{t("Actuel : {value}.", { value: enemyField.current })}</p>
            )}
          </ApplyRow>
        )}
        {weather && (
          <ApplyRow
            label={t("Météo")}
            checked={weatherChanges}
            disabled={!weatherApplicable}
            onChange={weatherApplicable ? setWeatherOn : undefined}
          >
            {centac ? (
              <span className="text-ink-soft">{t("Rotation CENTAC : la météo se rédige dans l'ordre de la rotation.")}</span>
            ) : (
              <>
                <span className="font-semibold">{weatherLabel(weather.proposed.label)}</span>
                <span className="text-ink-muted"> — {t("météo de l'ordre du chef")}</span>
                {!weather.applicable && <span className="text-ink-muted"> · {t("déjà dans le dossier")}</span>}
              </>
            )}
            {!centac && weather.notes.map((note) => (
              <p key={note} className="mt-0.5 text-meta text-ink-muted">
                {tServer(note)}
              </p>
            ))}
            {!centac && weather.applicable && (
              <p className="mt-0.5 text-meta text-ink-muted">{t("Actuelle : {value}.", { value: weatherLabel(weather.current.label) })}</p>
            )}
          </ApplyRow>
        )}
      </div>
      <footer className="flex flex-wrap items-center gap-3 border-t border-line px-4 py-3">
        <div className="min-w-0 flex-1 text-meta leading-snug text-ink-muted">
          {order.applied ? (
            <span className="inline-flex items-start gap-1.5 text-ok">
              <Check size={14} className="mt-px shrink-0" />
              <span>
                {t("Reporté {when}{terminal} : {fields}.", {
                  when: when(order.applied.at),
                  terminal: order.applied.terminal ? ` (${order.applied.terminal})` : "",
                  fields: order.applied.fields.map((x) => `${tServer(x.label).toLowerCase()} ${appliedValue(x)}`).join(", "),
                })}
              </span>
            </span>
          ) : resetsLayout ? (
            t("Le dispositif change : les déplacements manuels d'unités seront réinitialisés, comme dans les Données initiales.")
          ) : concordant && count === 0 ? (
            <span className="inline-flex items-start gap-1.5">
              <Check size={14} className="mt-px shrink-0 text-ok" /> {t("Les Données initiales concordent déjà avec cet ordre.")}
            </span>
          ) : (
            t("Cochez ce qui doit remplir les Données initiales.")
          )}
          {count > 0 && draftPending(exercise) && (
            <span className="mt-1 flex items-center gap-1 text-warn">
              <AlertTriangle size={14} /> {t("Vos modifications non enregistrées des Données initiales sont gardées, sauf pour les données reportées.")}
            </span>
          )}
        </div>
        <Button
          disabled={apply.isPending || (count === 0 && concordant)}
          blocked={writeBlock.blocked || (count === 0 && !concordant)}
          onBlocked={() => {
            if (writeBlock.blocked) return designateDossierReadOnly();
            // Rien n'est choisi : la sélection est l'action attendue (R12) ; quand tout concorde déjà, le bouton
            // reste désactivé, son explication est affichée à côté.
            designate(attentionSelector(applyTarget));
            notify({ text: t("Cochez ce qui doit remplir les Données initiales."), tone: "info" });
          }}
          onClick={() => apply.mutate()}
          highlight={count > 0 && !order.applied && !writeBlock.blocked}
        >
          {apply.isPending ? <Loader2 size={14} className="animate-spin" /> : <ArrowRightLeft size={14} />}
          {t("Reporter")}
          {count > 0 && <Badge size="sm">{count}</Badge>}
        </Button>
      </footer>
    </Card>
  );
}

const FACTIONS: Record<EnemyType, string> = { Titan: "TI", Mercure: "ME", Titane: "TN", Tantale: "TA", Deimos: "DE" };
const factionCode = (type: EnemyType | null) => (type ? FACTIONS[type] : "?");

// --- Suggestions du modèle local ---------------------------------------------------------------------------------

function SuggestionsCard({ exerciseId, order, citedIntention }: { exerciseId: string; order: ReceivedOrder; citedIntention: boolean }) {
  // Dossier en lecture seule pour le compte (docs/17 §3.2) : écritures bloquées, l'appui désigne le bandeau.
  const writeBlock = useWriteBlock();
  const queryClient = useQueryClient();
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ chars: number; attempt: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  useEffect(() => () => abort.current?.abort(), []);
  const s: ReceivedSuggestions | null = order.suggestions;

  const run = async () => {
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    setRunning(true);
    setError(null);
    setProgress({ chars: 0, attempt: 1 });
    try {
      await streamReceivedSuggestions(
        exerciseId,
        order.id,
        (e) => {
          if (e.event === "progress") setProgress({ chars: e.data.chars ?? 0, attempt: e.data.attempt ?? 1 });
          else if (e.event === "retry") setProgress({ chars: 0, attempt: e.data.attempt ?? 2 });
          else if (e.event === "error") setError(e.data.message != null ? tServer(e.data.message) : t("Échec du modèle."));
          else if (e.event === "done" && e.data.suggestions) {
            queryClient.setQueryData<ReceivedOrder>(["received", exerciseId, order.id], (o) => (o ? { ...o, suggestions: e.data.suggestions! } : o));
            void queryClient.invalidateQueries({ queryKey: ["received", exerciseId], exact: true });
          }
        },
        controller.signal,
      );
    } catch (e) {
      if (!controller.signal.aborted) setError((e as Error).message);
    } finally {
      if (abort.current === controller) {
        setRunning(false);
        setProgress(null);
      }
    }
  };

  return (
    <Card
      title={t("Suggestions du modèle local")}
      icon={<Sparkles size={14} className="text-run" />}
      trailing={
        <Button size="sm" variant={s ? "secondary" : "primary"} disabled={running} onClick={run} {...writeBlock}>
          {running ? <Loader2 size={14} className="animate-spin" /> : s ? <RefreshCw size={14} /> : <Sparkles size={14} />}
          {running ? <Waiting>{t("Analyse en cours…")}</Waiting> : s ? t("Relancer") : t("Demander au modèle")}
        </Button>
      }
    >
      <div className="space-y-3 px-4 py-3">
        <Presence show={Boolean(running && progress)}>
          {running && progress && (
            <Motion origin="fade" className="flex items-center gap-2 text-ui text-ink-muted">
              <Spinner tone="run" decorative />
              <Waiting>
                {progress.attempt > 1 ? t("Réponse illisible : nouvel essai ({attempt}/3)…", { attempt: progress.attempt }) : t("Le modèle lit l'ordre…")}
              </Waiting>{" "}
              {progress.chars > 0 && t("{n} caractères", { n: progress.chars.toLocaleString(locale()) })}
            </Motion>
          )}
        </Presence>
        <Presence show={Boolean(error)}>{error && <Callout tone="danger">{error}</Callout>}</Presence>
        {s && order.suggestions_translating && !running ? (
          // Suggestions rédigées dans l'autre langue (docs/13) : leur traduction arrive, attente animée à leur place.
          <p className="flex items-center gap-2 text-ui text-ink-muted">
            <Spinner tone="run" decorative />
            <Waiting>{t("Traduction…")}</Waiting>
          </p>
        ) : s ? (
          <div className={clsx("space-y-3 transition-opacity", running && "opacity-50")}>
            {s.resume && <p className="text-left text-title leading-relaxed text-ink hyphens-auto sm:text-justify">{s.resume}</p>}
            {s.intention && !citedIntention && (
              <div>
                <SectionLabel inset>{t("Intention proposée (absente de l'ordre)")}</SectionLabel>
                <p className="mt-0.5 text-title leading-relaxed text-ink">{s.intention}</p>
              </div>
            )}
            {[
              ...s.taches_implicites.map((task) => [k("tâche implicite"), task] as const),
              ...s.contraintes.map((c) => [k("contrainte"), c] as const),
            ].map(([badge, text]) => (
              <div key={`${badge}-${text}`} className="flex items-start gap-2">
                <Badge size="sm" tone="accent" className="mt-px">
                  {t(badge)}
                </Badge>
                <span className="text-ui leading-snug text-ink">{text}</span>
              </div>
            ))}
            <p className="text-meta text-ink-muted">
              {t("Proposé par {source} {when} — à vérifier avant emploi : rien n'est reporté dans le dossier.", {
                source: s.source != null ? tServer(s.source) : t("le modèle local"),
                when: when(s.at),
              })}
            </p>
          </div>
        ) : (
          !running &&
          !error && (
            <p className="text-ui leading-relaxed text-ink-soft">
              {t(
                "Le modèle local peut proposer une synthèse de l'ordre, l'intention du chef si elle n'est pas citée, des tâches implicites et des contraintes (rien d'inventé, cinq éléments au plus).",
              )}
            </p>
          )
        )}
      </div>
    </Card>
  );
}

// --- Fiche d'un ordre ---------------------------------------------------------------------------------------------

function OrderDetail({
  exercise,
  orderId,
  onDeleted,
  focus,
  onFocused,
}: {
  exercise: Exercise;
  orderId: string;
  onDeleted: () => void;
  /** Ouverture demandée depuis l'avis de l'ordre du chef : passages changés (`view`) ou report (`apply`). */
  focus?: ReceivedFocus["mode"] | null;
  onFocused?: () => void;
}) {
  // Dossier en lecture seule pour le compte (docs/17 §3.2) : écritures bloquées, l'appui désigne le bandeau.
  const writeBlock = useWriteBlock();
  // Licence d'essai (registre F44) : l'original reçu ne se télécharge pas (il peut être le PDF d'un OPO transmis).
  const trialExport = useTrialExport();
  const queryClient = useQueryClient();
  const notify = useUi((s) => s.notify);
  const key = ["received", exercise.id, orderId] as const;
  const query = useQuery({
    queryKey: key,
    queryFn: () => api.receivedOrder(exercise.id, orderId),
    retry: false,
    // Suggestions lues dans l'autre langue (docs/13) : relues toutes les 2,5 s tant que leur traduction attend.
    refetchInterval: (q) => translationInterval(Boolean(q.state.data?.suggestions_translating), false),
  });
  const [renaming, setRenaming] = useState<string | null>(null);
  const renameRef = useRef<HTMLInputElement>(null);
  // Nom vide : le champ du nom est l'action attendue (R12).
  const nameFirst = () => {
    designate(renameRef.current, "field");
    notify({ text: t("Donnez un nom à l'ordre reçu."), tone: "info" });
  };
  const [confirm, setConfirm] = useState(false);
  const rename = useMutation({
    mutationFn: (name: string) => api.renameReceivedOrder(exercise.id, orderId, name),
    onSuccess: (next) => {
      queryClient.setQueryData(key, next);
      void queryClient.invalidateQueries({ queryKey: ["received", exercise.id], exact: true });
      setRenaming(null);
    },
    onError: (e: Error) => notify({ text: t("Renommage impossible"), detail: e.message, tone: "error" }),
  });
  const remove = useMutation({
    mutationFn: () => api.deleteReceivedOrder(exercise.id, orderId),
    onSuccess: () => {
      setConfirm(false);
      queryClient.removeQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: ["received", exercise.id], exact: true });
      onDeleted();
    },
    onError: (e: Error) => notify({ text: t("Suppression impossible"), detail: e.message, tone: "error" }),
  });

  const order = query.data;
  // Ordres transmis prévenus : lignes changées depuis la dernière version vue de l'ordre du chef, surlignées.
  const changed = useMemo(() => changedSet(order?.notice?.changed), [order?.notice?.changed]);
  const loaded = Boolean(order);
  useEffect(() => {
    if (!loaded || !focus) return;
    // Après le rendu de la fiche : le report est désigné (action attendue), ou le premier passage changé vient à
    // l'écran.
    const frame = window.requestAnimationFrame(() => {
      if (focus === "apply") designate(attentionSelector(`received-apply-${orderId}`));
      else document.querySelector("[data-changed-line]")?.scrollIntoView({ block: "center", behavior: "smooth" });
      onFocused?.();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [loaded, focus, orderId, onFocused]);
  if (query.isError) return <EmptyState variant="error">{query.error.message}</EmptyState>;
  if (!order) return <EmptyState variant="loading" title={t("Analyse de l'ordre…")} />;
  const a = order.analysis;
  const missionQuote = a.verb?.in_mission ? a.verb.quote : null;
  const meta = [
    tServer(order.kind_label),
    order.pages ? plural(order.pages, t("page"), t("pages")) : null,
    t("{n} caractères", { n: order.chars.toLocaleString(locale()) }),
    order.created_by
      ? t("importé {when} par {name}", { when: when(order.created_at), name: order.created_by })
      : t("importé {when}", { when: when(order.created_at) }),
  ].filter(Boolean);

  const warned = order.blank_pages.length > 0 || order.truncated || a.empty;
  return (
    <div key={order.id} className={motion("fade")}>
      {/* État durable de l'ordre lu : pages sans texte, document tronqué, structure non reconnue (bandeau, R10). */}
      <Presence show={warned}>
        {warned && (
          <Banner tone="warn">
            {order.blank_pages.length > 0 && (
              <p>
                {order.blank_pages.length === 1
                  ? t("Page {page} sans texte (numérisée) : son contenu n'est pas analysé.", { page: order.blank_pages[0] })
                  : t("Pages {pages} sans texte (numérisées) : son contenu n'est pas analysé.", { pages: order.blank_pages.join(", ") })}
              </p>
            )}
            {order.truncated && <p>{t("Document long : seul son début a été lu (120 pages ou 400 000 caractères au plus).")}</p>}
            {a.empty && (
              <p>
                {t(
                  "Aucune structure d'ordre reconnue : ni paragraphe « Situation », « Mission » ou « Exécution », ni verbe de mission. Vérifiez le document.",
                )}
              </p>
            )}
          </Banner>
        )}
      </Presence>
      <div className="mx-auto w-full max-w-[980px] space-y-4 px-6 py-5 max-sm:px-4">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            {renaming !== null ? (
              <form
                className="flex items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (renaming.trim()) rename.mutate(renaming.trim());
                  else nameFirst();
                }}
              >
                <input ref={renameRef} autoFocus value={renaming} maxLength={200} onChange={(e) => setRenaming(e.target.value)} className={inputTitleClass} />
                <Button type="submit" disabled={rename.isPending} blocked={!renaming.trim()} onBlocked={nameFirst}>
                  {t("Renommer")}
                </Button>
                <Button variant="secondary" onClick={() => setRenaming(null)}>
                  {t("Annuler")}
                </Button>
              </form>
            ) : (
              <h2 className="flex items-center gap-1.5 text-heading font-semibold text-ink">
                <span className="truncate">{order.name}</span>
                <IconButton size="sm" onClick={() => setRenaming(order.name)} title={t("Renommer")} aria-label={t("Renommer")} {...writeBlock}>
                  <Pencil size={14} />
                </IconButton>
              </h2>
            )}
            <p className="mt-0.5 text-meta text-ink-muted">{meta.join(" · ")}</p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {order.has_original && (
              <ButtonLink variant="secondary" href={api.receivedOriginalUrl(exercise.id, order.id)} target="_blank" rel="noreferrer" {...trialExport}>
                <ExternalLink size={14} /> {order.kind === "pdf" ? t("Ouvrir l'original") : t("Télécharger l'original")}
              </ButtonLink>
            )}
            <IconButton
              variant="danger"
              onClick={() => setConfirm(true)}
              {...writeBlock}
              title={t("Supprimer l'ordre reçu")}
              aria-label={t("Supprimer l'ordre reçu")}
            >
              <Trash2 size={14} />
            </IconButton>
          </div>
        </div>

        {/* Ordre du chef mis à jour depuis la dernière version vue (ordres transmis prévenus) : rien n'est reporté
            d'office, les passages changés sont surlignés. */}
        <Presence show={Boolean(order.notice)}>
          {order.notice && (
            <Callout tone="info">
              {plural(order.notice.paragraphs, t("paragraphe mis à jour"), t("paragraphes mis à jour"))}{" "}
              {order.notice.issuer
                ? t("depuis la transmission de l'ordre de « {chef} », surlignés ci-dessous. Reportez ce qui convient : rien n'est reporté d'office.", {
                    chef: unitDesignation(order.notice.issuer),
                  })
                : t("depuis la transmission, surlignés ci-dessous. Reportez ce qui convient : rien n'est reporté d'office.")}
            </Callout>
          )}
        </Presence>

        <ApplyCard key={order.id} exercise={exercise} order={order} />

        <Card title={t("Ordre structuré")} icon={<FileText size={14} className="text-ink-muted" />} trailing={<span className="text-meta text-ink-muted">{t("cité tel quel")}</span>}>
          {a.sections.map((s) => (
            <Collapsible
              key={s.key}
              title={tServer(s.label)}
              defaultOpen={s.key === "mission" || markLines(s.text, changed).some((l) => l.changed)}
              trailing={<span className="text-meta text-ink-muted tabular-nums">{t("{n} car.", { n: s.text.length.toLocaleString(locale()) })}</span>}
            >
              <Quoted text={s.text} highlight={s.key === "mission" ? missionQuote : null} changed={changed} />
            </Collapsible>
          ))}
          {a.annex && <p className="px-4 py-2.5 text-meta text-ink-muted">{t("Les annexes ne sont pas découpées : elles figurent dans le texte intégral.")}</p>}
        </Card>

        {(a.intention || a.tasks.length > 0 || a.constraints.length > 0) && (
          <Card title={t("Intention, tâches et contraintes citées")} icon={<Clock size={14} className="text-ink-muted" />}>
            <div className="space-y-4 px-4 py-3">
              {a.intention && (
                <div>
                  <SectionLabel inset>
                    {t("Intention du chef")} {a.intention_from_section ? t("(paragraphe de l'ordre)") : t("(phrase « Je veux… » de l'ordre)")}
                  </SectionLabel>
                  <blockquote className="mt-1 border-l-2 border-accent pl-3 text-left text-title leading-relaxed text-ink italic hyphens-auto sm:text-justify">
                    {a.intention}
                  </blockquote>
                </div>
              )}
              {a.tasks.length > 0 && (
                <div>
                  <SectionLabel inset>{t("Tâches")}</SectionLabel>
                  <ul className="mt-1 space-y-1.5">
                    {a.tasks.map((task) => (
                      <li key={task.text} className="flex items-start gap-2">
                        {task.verb ? (
                          // Verbe classé : étiquette de l'interface, dans la langue choisie (lexique OTAN en anglais).
                          <Badge size="sm" tone="friend" className="mt-px">
                            {unitMissionName(task.verb)}
                          </Badge>
                        ) : (
                          <span className="mt-2 size-1.5 shrink-0 rounded-full bg-line-strong" />
                        )}
                        <span className="text-ui leading-snug text-ink">{task.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {a.constraints.length > 0 && (
                <div>
                  <SectionLabel inset>{t("Contraintes de temps et de coordination")}</SectionLabel>
                  <ul className="mt-1 space-y-1.5">
                    {a.constraints.map((c) => (
                      <li key={c} className="flex items-start gap-2">
                        <Clock size={14} className="mt-0.5 shrink-0 text-warn" />
                        <span className="text-ui leading-snug text-ink">{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Card>
        )}

        <SuggestionsCard exerciseId={exercise.id} order={order} citedIntention={Boolean(a.intention)} />

        <Card title={t("Texte intégral")} icon={<FileText size={14} className="text-ink-muted" />}>
          <Collapsible title={t("Texte lu dans le document")} trailing={<span className="text-meta text-ink-muted tabular-nums">{t("{n} car.", { n: order.chars.toLocaleString(locale()) })}</span>}>
            <pre className="max-h-[480px] overflow-auto rounded-lg bg-canvas/60 px-3 py-2 font-sans text-ui leading-relaxed whitespace-pre-wrap text-ink-soft">
              <MarkedLines text={order.text} changed={changed} />
            </pre>
          </Collapsible>
        </Card>

        <Presence show={confirm}>
          {confirm && (
            <ConfirmDialog
              title={t("Supprimer l'ordre reçu ?")}
              confirmLabel={t("Supprimer")}
              busy={remove.isPending}
              busyLabel={t("Suppression…")}
              onConfirm={() => remove.mutate()}
              onCancel={() => setConfirm(false)}
            >
              {t("« {name} », son analyse et ses suggestions seront supprimés. Les Données initiales déjà reportées ne changent pas.", {
                name: order.name,
              })}
            </ConfirmDialog>
          )}
        </Presence>
      </div>
    </div>
  );
}

// --- Liste et vue ----------------------------------------------------------------------------------------------------

function OrderCard({ order, active, onClick }: { order: ReceivedSummary; active: boolean; onClick: () => void }) {
  const h = order.headline;
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "flex w-full flex-col gap-1.5 rounded-xl border px-3 py-2.5 text-left transition-colors",
        active ? "border-accent bg-accent-soft" : "border-line bg-surface hover:bg-hover",
      )}
    >
      <span className="flex items-center gap-2">
        <FileText size={14} className="shrink-0 text-ink-muted" />
        <span className="min-w-0 flex-1 truncate text-ui font-semibold text-ink">{order.name}</span>
        {order.applied_at && (
          <span title={t("Reporté dans les Données initiales {when}", { when: when(order.applied_at) })}>
            <Check size={14} className="text-ok" />
          </span>
        )}
        {order.updated_since && (
          <Badge size="sm" tone="accent">
            {t("Mis à jour")}
          </Badge>
        )}
      </span>
      <span className="text-meta text-ink-muted">
        {tServer(order.kind_label)}
        {order.pages ? ` · ${order.pages} p.` : ""} · {when(order.created_at)}
      </span>
      {(h.issuer || h.verb || h.enemy) && (
        <span className="flex flex-wrap gap-1">
          {h.issuer && <Badge size="sm">{issuerLabel(h.issuer, h.echelon)}</Badge>}
          {h.verb && (
            <Badge size="sm" tone="friend">
              {unitMissionName(h.verb)}
            </Badge>
          )}
          {h.enemy && (
            <Badge size="sm" tone="hostile">
              {h.enemy}
            </Badge>
          )}
        </span>
      )}
    </button>
  );
}

export function ReceivedOrdersView() {
  // Dossier en lecture seule pour le compte (docs/17 §3.2) : écritures bloquées, l'appui désigne le bandeau.
  const writeBlock = useWriteBlock();
  const exerciseId = useUi((s) => s.exerciseId);
  const notify = useUi((s) => s.notify);
  const queryClient = useQueryClient();
  const { data: exercises = [] } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const exercise = exercises.find((e) => e.id === exerciseId);
  const list = useQuery({
    queryKey: ["received", exerciseId],
    queryFn: () => api.receivedOrders(exerciseId as string),
    enabled: Boolean(exerciseId),
  });
  const orders = list.data ?? [];
  const [selected, setSelected] = useState<string | null>(null);
  // Ouverture demandée depuis l'avis de l'ordre du chef (ordres transmis prévenus) : cet ordre reçu est choisi.
  const focus = useReceivedFocus((s) => s.focus);
  const setFocus = useReceivedFocus((s) => s.setFocus);
  const focused = focus && focus.exerciseId === exerciseId ? focus : null;
  const [focusSeen, setFocusSeen] = useState<ReceivedFocus | null>(null);
  if (focused && focused !== focusSeen) {
    setFocusSeen(focused);
    setSelected(focused.orderId);
  }
  const current = orders.find((o) => o.id === selected) ?? orders[0] ?? null;
  const clearFocus = useCallback(() => setFocus(null), [setFocus]);
  const [importing, setImporting] = useState(false);
  const [dropped, setDropped] = useState<File | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  // Tablette (sous 1280 px) : la liste passe dans un tiroir, ouvert à la demande, et le détail prend toute la
  // largeur (comme les ordres de bataille).
  const wide = useMediaQuery("(min-width: 1280px)");
  const [listOpen, setListOpen] = useState(false);
  useDismiss(listOpen && !wide, () => setListOpen(false));

  const upload = useMutation({
    mutationFn: ({ file, text, name }: { file: File | null; text: string; name: string }) =>
      file
        ? api.uploadReceivedOrder(exerciseId as string, file, name)
        : api.pasteReceivedOrder(exerciseId as string, text, name || t("Ordre reçu {when}", { when: when(new Date().toISOString()) })),
    onSuccess: (order) => {
      queryClient.setQueryData(["received", exerciseId, order.id], order);
      void queryClient.invalidateQueries({ queryKey: ["received", exerciseId], exact: true });
      setSelected(order.id);
      setImporting(false);
      setDropped(null);
      const sections = order.analysis.sections.filter((s) => s.key !== "preamble").length;
      notify({
        text: t("Ordre importé"),
        detail: order.analysis.empty
          ? t("Aucune structure d'ordre reconnue : vérifiez le document.")
          : t("{paragraphs} — vérifiez puis reportez ce qui convient.", {
              paragraphs: plural(sections, t("paragraphe reconnu"), t("paragraphes reconnus")),
            }),
        tone: order.analysis.empty ? "warn" : "ok",
      });
    },
    onError: (e: Error) => setImportError(e.message),
  });

  const openImport = (file: File | null = null) => {
    setDropped(file);
    setImportError(null);
    upload.reset();
    setImporting(true);
  };

  const ready = Boolean(exerciseId && exercise);
  const drop = useFileDrop(ready && !importing && !writeBlock.blocked, (file) => openImport(file));
  const hasList = ready && orders.length > 0;
  const cards = orders.map((o) => (
    <OrderCard
      key={o.id}
      order={o}
      active={o.id === current?.id}
      onClick={() => {
        setSelected(o.id);
        setListOpen(false);
      }}
    />
  ));

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-canvas" {...drop.handlers}>
      <ViewHeader
        view="received"
        context={exercise?.name}
        badges={orders.length > 0 ? <Badge>{orders.length}</Badge> : null}
        actions={
          hasList && (
            <Button size="sm" onClick={() => openImport()} {...writeBlock}>
              <FileUp size={14} /> {t("Importer un ordre")}
            </Button>
          )
        }
        panelToggle={
          hasList &&
          !wide && (
            <IconButton
              size="sm"
              onClick={() => setListOpen((o) => !o)}
              active={listOpen}
              title={t("Liste des ordres reçus")}
              aria-label={t("Liste des ordres reçus")}
            >
              <List size={16} />
            </IconButton>
          )
        }
      />

      {/* Dossier transmis : l'ordre de son chef a changé depuis la transmission (ordres transmis prévenus). */}
      <ChiefOrderBanner exercise={exercise} />

      <div className="relative min-h-0 flex-1">
        {!ready ? (
          <DossierEmptyState view="received" kind="none">
            {t("Les ordres reçus d'un échelon supérieur se rangent dans le dossier : leur analyse aide à remplir les Données initiales.")}
          </DossierEmptyState>
        ) : list.isError ? (
          <EmptyState variant="error">{list.error.message}</EmptyState>
        ) : !list.data ? (
          <EmptyState variant="loading" title={t("Chargement des ordres reçus…")} />
        ) : orders.length === 0 ? (
          <EmptyState
            icon={FileUp}
            title={t("Aucun ordre reçu")}
            action={
              <Button onClick={() => openImport()} highlight={!writeBlock.blocked} {...writeBlock}>
                <FileUp size={14} /> {t("Importer un ordre")}
              </Button>
            }
            note={t("PDF, Word, LibreOffice, RTF, texte — ou texte collé. Vous pouvez aussi déposer le fichier ici.")}
          >
            {t(
              "Importez l'ordre reçu de l'échelon supérieur : il est découpé en paragraphes, sa mission, son émetteur, l'ennemi, les tâches et les contraintes sont relevés tels qu'écrits, puis vous choisissez ce qui remplit les Données initiales.",
            )}
          </EmptyState>
        ) : (
          <div className="flex h-full min-h-0">
            {wide ? (
              <aside className="w-[300px] shrink-0 space-y-2 overflow-y-auto border-r border-line bg-canvas px-3 py-3">{cards}</aside>
            ) : (
              // Tiroir de la liste sous 1280 px : glisse depuis la gauche, voile en fondu qui le ferme au toucher.
              <Presence show={listOpen}>
                {listOpen && (
                  <>
                    <Motion origin="fade" className="absolute inset-0 z-30 bg-ink/20" onClick={() => setListOpen(false)} aria-hidden />
                    <Motion
                      origin="left"
                      className="absolute inset-y-0 left-0 z-40 w-[300px] max-w-[85%] space-y-2 overflow-y-auto border-r border-line bg-canvas px-3 py-3 shadow-float"
                    >
                      {cards}
                    </Motion>
                  </>
                )}
              </Presence>
            )}
            <main className="min-w-0 flex-1 overflow-y-auto">
              {current && exercise && (
                <OrderDetail
                  key={current.id}
                  exercise={exercise}
                  orderId={current.id}
                  focus={focused?.orderId === current.id ? focused.mode : null}
                  onFocused={clearFocus}
                  onDeleted={() => {
                    setSelected(null);
                    notify({ text: t("Ordre reçu supprimé"), tone: "info" });
                  }}
                />
              )}
            </main>
          </div>
        )}

        <DropZone show={drop.dragging} label={t("Déposez l'ordre pour l'importer")} />
      </div>

      <Presence show={importing}>
        {importing && (
          <ImportDialog
            initialFile={dropped}
            busy={upload.isPending}
            error={importError}
            onImport={(input) => {
              setImportError(null);
              upload.mutate(input);
            }}
            onClose={() => {
              if (!upload.isPending) setImporting(false);
            }}
          />
        )}
      </Presence>
    </div>
  );
}
